import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import {
  addCaseDefendantLink,
  editCaseDefendantLink,
} from "services/caseServices";
import {
  CaseCreation,
  CaseDefendantLink,
  Reporter,
  Suspect,
  Victim,
  Witness,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandler, errorHandlerOnForm } from "utils/helper";

type Props = {
  caseData: CaseCreation;
  action: "add" | "edit";
  selectedData?: CaseDefendantLink;
  caseRelatedParties: {
    suspects: Suspect[];
    witnesses: Witness[];
    victims: Victim[];
    reporters: Reporter[];
  };
  onClose: (refetch: boolean) => void;
};

const DefendantLinkForm: FC<Props> = ({
  caseData,
  action,
  selectedData,
  caseRelatedParties,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);

  const [defendant, setDefendant] = useState<string>();
  const [defendantNumber, setDefendantNumber] = useState<string>();

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  useEffect(() => {
    if (selectedData) {
      setDefendant(
        selectedData?.defendant_info?.uu_id
          ? String(selectedData.defendant_info.uu_id)
          : "",
      );
      setDefendantNumber(
        selectedData?.defendant_number !== undefined
          ? String(selectedData.defendant_number)
          : "",
      );
    }
  }, [selectedData]);

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 w-[600px]">
        <h2 className="text-lg font-semibold text-gray-800">Add Defendants</h2>

        <div className="flex flex-col mt-4">
          <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
            Select Defendants
          </label>
          <select
            className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
            value={defendant}
            onChange={(e) => {
              setDefendant(e.target.value);
            }}
            disabled={action === "edit"}
          >
            <option value="">Select Defendant</option>
            {caseRelatedParties?.suspects
              ?.filter(
                (a) =>
                  !caseData?.case_defendant_links?.find(
                    (d) => d?.defendant_info?.uu_id === a?.uu_id,
                  ) && a?.closed_by_42 !== true,
              )
              .map((f) => (
                <option key={f?.uu_id} value={f?.uu_id}>
                  {f.first_name} {f.middle_name} {f.last_name}
                </option>
              ))}
          </select>
        </div>
        <div className="flex flex-col mt-4 w-full">
          <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
            Defendant Number
          </label>
          <input
            className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
            placeholder="Enter Defendant Number"
            value={defendantNumber}
            type="number"
            min={1}
            onChange={(e) => setDefendantNumber(e.target.value)}
          />
        </div>

        <div className="mt-4 flex justify-between gap-2">
          <button
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded w-full hover:bg-gray-300"
            onClick={() => {
              onClose(false);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`border border-[#E90101] bg-[#E90101] px-10 py-2 text-white 
                                  font-[Poppins] cursor-pointer font-[500] w-full
                                  text-base rounded-md hover:bg-white hover:text-[#E90101]
                                  ${
                                    loading
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
            disabled={loading}
            onClick={() => {
              setLoading(true);
              const api =
                action === "add" ? addCaseDefendantLink : editCaseDefendantLink;
              api({
                id: action === "add" ? undefined : selectedData?.id,
                defendant_number: defendantNumber,
                case: caseData?.id,
                defendant: defendant,
              })
                .then(() => {
                  setLoading(false);
                  showTooltip("Successful!", "success");
                  setDefendantNumber("");
                  setDefendant("");
                  onClose(true);
                })
                .catch((error) => {
                  setLoading(false);
                  showTooltip(
                    error?.response?.data?.detail ||
                      errorHandlerOnForm(error) ||
                      errorHandler(error) ||
                      "Something went wrong. Please try again.",
                    "error",
                  );
                });
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                {LoadingIcon}
                Loading...
              </div>
            ) : action === "add" ? (
              "Add"
            ) : (
              "Edit"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DefendantLinkForm;
